import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Zap, Gift } from "lucide-react";
import api from "@/lib/api";
import GoogleSignInButton from "@/components/GoogleSignInButton";

// Indian states / UTs — plus common alternates so users abroad still work.
const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman & Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry", "Other",
];

export default function Register() {
  const { register, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [refInfo, setRefInfo] = useState(null); // {name, code}
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    phone: "", address: "", state: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Resolve the referrer's name for the hero banner (silent fail = no banner).
  useEffect(() => {
    if (!refCode || refCode.length < 4) return;
    api.get(`/referral/lookup/${refCode}`).then((r) => setRefInfo(r.data)).catch(() => setRefInfo(null));
  }, [refCode]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onGoogleAuth = async () => {
    await refresh?.();
    toast.success("Signed up with Google");
    navigate("/dashboard");
  };

  // Countdown for resend button while on step 2
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const resend = async () => {
    if (resendIn > 0 || resending) return;
    setErr(""); setResending(true);
    try {
      const { data } = await (await import("@/lib/api")).default.post("/auth/register-start", { phone: form.phone });
      if (data.dev_code) toast.success(`DEV MODE OTP: ${data.dev_code}`);
      else toast.success("OTP re-sent to your phone");
      setResendIn(30);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not resend OTP");
    } finally { setResending(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (step === 1) {
      if (!form.state) { setErr("Please select a state"); return; }
      setLoading(true);
      try {
        const { data } = await (await import("@/lib/api")).default.post("/auth/register-start", { phone: form.phone });
        if (data.dev_code) {
          toast.success(`DEV MODE OTP: ${data.dev_code}`);
        } else {
          toast.success("OTP sent to your phone");
        }
        setResendIn(30);
        setStep(2);
      } catch (e2) {
        setErr(e2.response?.data?.detail || "Could not send OTP");
      } finally { setLoading(false); }
      return;
    }
    // Step 2: verify + create
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      const { data } = await api.post("/auth/register-verify", { ...form, code: otp, referral_code: refCode || undefined });
      toast.success(refInfo ? `Welcome ${data.name} — 7-day bonus unlocks on your first attempt!` : `Welcome, ${data.name}`);
      window.location.href = "/dashboard";
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-lg" data-testid="register-form">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#002FA7] mb-4">Create account</div>
          <h2 className="text-4xl font-black tracking-tight mb-2">Register</h2>
          <p className="font-mono text-sm text-neutral-500 mb-6">Free forever for students.</p>

          {/* Referral hero banner — shown when arriving via /register?ref=CODE */}
          {refInfo && (
            <div className="mb-6 border-2 border-[#002FA7] bg-[#F1F5FF] p-4 flex items-start gap-3" data-testid="register-referral-banner">
              <Gift className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7]">Referral bonus locked</div>
                <div className="font-mono text-sm text-neutral-800 mt-1">
                  <b>{refInfo.name}</b> ne aapko invite kiya hai. Signup karo aur pehli practice complete karte hi dono ko <b>7 din ka bonus</b> milega!
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <>
              <GoogleSignInButton onAuth={onGoogleAuth} referralCode={refCode} text="signup_with" />
              <div className="relative mb-4">
                <div className="h-px bg-black/10"></div>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-background px-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">or continue with phone</div>
              </div>
            </>
          )}

          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-bold">Full Name</Label>
                <Input
                  required maxLength={100}
                  value={form.name} onChange={set("name")}
                  className="mt-2 rounded-none border-black/20 h-11 font-mono"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-bold">Phone Number</Label>
                <Input
                  required minLength={7} maxLength={20}
                  value={form.phone} onChange={set("phone")}
                  placeholder="+91 98xxxxxxxx"
                  className="mt-2 rounded-none border-black/20 h-11 font-mono"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Email</Label>
              <Input
                type="email" required
                value={form.email} onChange={set("email")}
                className="mt-2 rounded-none border-black/20 h-11 font-mono"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Password (min 8 chars)</Label>
              <Input
                type="password" required minLength={8} maxLength={128}
                value={form.password} onChange={set("password")}
                className="mt-2 rounded-none border-black/20 h-11 font-mono"
                data-testid="register-password-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Address</Label>
              <Textarea
                required minLength={1} maxLength={300}
                value={form.address} onChange={set("address")}
                placeholder="House / Street / City / PIN"
                rows={2}
                className="mt-2 rounded-none border-black/20 font-mono resize-none"
                data-testid="register-address-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">State</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger
                  className="mt-2 rounded-none border-black/20 h-11 font-mono"
                  data-testid="register-state-input"
                >
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {STATES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`register-state-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {err && (
              <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="register-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (step === 1 && !form.state)}
              className="w-full rounded-none h-12 bg-[#0A2540] hover:bg-[#002FA7] text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
              data-testid="register-submit-btn"
            >
              {loading ? "Please wait…" : step === 1 ? "Send OTP" : "Verify & Create account"}
            </Button>
            {step === 2 && (
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-[0.2em] font-bold">OTP (6 digits)</Label>
                <Input
                  required minLength={4} maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="mt-2 rounded-none border-black/20 h-12 font-mono text-center tracking-widest text-lg"
                  data-testid="register-otp-input"
                />
                <div className="mt-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(""); setErr(""); setResendIn(0); }}
                    data-testid="register-otp-back"
                    className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-[#002FA7]"
                  >
                    ← Change phone / edit details
                  </button>
                  <button
                    type="button"
                    disabled={resendIn > 0 || resending}
                    onClick={resend}
                    data-testid="register-otp-resend"
                    className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] disabled:text-neutral-400 disabled:cursor-not-allowed hover:underline"
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : resending ? "Sending…" : "Resend OTP"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 text-sm font-mono text-neutral-500">
            Already have an account?{" "}
            <Link to="/login" className="text-[#002FA7] font-bold underline underline-offset-4" data-testid="register-goto-login">
              Log in
            </Link>
          </div>
        </form>
      </div>

      <div className="hidden lg:flex flex-col justify-between bg-[#0A2540] text-white p-12 order-1 lg:order-2">
        <Link to="/" className="flex items-center gap-2 self-end" data-testid="register-logo">
          <div className="w-8 h-8 bg-white flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#0A2540]" />
          </div>
          <span className="font-black text-lg tracking-tight">StenoSync</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/50 mb-6">Join today</div>
          <h1 className="text-5xl font-black tracking-tight leading-none">
            Train like<br />
            a HIGH COURT PS.
          </h1>
          <p className="mt-6 font-mono text-white/70 text-sm">
            Aapki basic details safely store hoti hain aur sirf certificates aur reports par use hoti hain.
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">Four modules · One studio</div>
      </div>
    </div>
  );
}
